import cx from "classnames";
import React, { useEffect } from "react";
import { MySubscription } from "../../../../types/subscription/my-subscription-plan";
import { SubscriptionPlan } from "../../../../types/subscription/subscription-plan";
import { Button, TextInput } from "../../../../_components";
import I18 from "../../../../_components/atoms/i18";
import TextButton from "../../../../_components/atoms/text-button";
import DropDownSelector from "../../../../_components/compound/dopdown-selector";
import { Radio } from "../../../../_components/form-controls/radio.component";
import ModalForm from "../../../../_components/model2";
import Label from "../../../../_components/typography/label.typography";
import UIcon from "../../../../_components/uicon-component";
import { Countries } from "../../../../_config/states/countries";
import { IndianStates } from "../../../../_config/states/indian-states";
import { UsStates } from "../../../../_config/states/us-states";
import { BillingDetails } from "../../../../_store/subscription/my-subscription/my-subscription.reducer";
import { Fold } from "../../../../_utils/extensions/typescript-utils";
import { SubscriptionUtils } from "../../../../_utils/subscription/subscription-util";
import { CurrencySymbol } from "../component/subscription_tile.component";

interface BillingAddressProps {
  plan: any;
  user: any;
  community: any;
  existingBillingAddress?: BillingDetails;
  onProceed: (billingInfo: BillingDetails) => void;
}
/**
 * Get user's billing address before checkout
 * @param {any} plan - subscription plan
 * @param {any} user - user object
 * @param {any} community - community object
 * @param {Function} onProceed - callback function to proceed to checkout
 */
export function BillingAddress({
  plan,
  user,
  community,
  existingBillingAddress,
  onProceed,
}: BillingAddressProps) {
  const [email, setFullNameEmail] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [mobile, setMobile] = React.useState("");
  const [addressLine1, setAddressLine1] = React.useState("");
  const [addressLine2, setAddressLine2] = React.useState("");
  const [state, setState] = React.useState("");
  const [country, setCountry] = React.useState("");
  useEffect(() => {
    if (existingBillingAddress && Object.keys(existingBillingAddress).length) {
      setFullNameEmail(existingBillingAddress.email ?? "");
      setFullName(existingBillingAddress.fullName ?? "");
      setMobile(existingBillingAddress.mobile ?? "");
      setAddressLine1(existingBillingAddress.addressLine1 ?? "");
      setAddressLine2(existingBillingAddress.addressLine2 ?? "");
      setState(existingBillingAddress.state ?? "");
      setCountry(existingBillingAddress.country ?? "");
    } else if (user) {
      setFullNameEmail(user.email ?? "");
      setFullName(user.name ?? "");
      setMobile(user.mobile ?? "");
    }
  }, [existingBillingAddress, user]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const billingInfo = {
          email,
          fullName,
          mobile,
          addressLine1,
          addressLine2,
          state,
          country,
        };
        onProceed(billingInfo);
      }}
      className="flex w-full flex-col gap-4 bg-card p-4">
      <Label variant="t1" size="body1">
        Billing Details
      </Label>
      <div className="flex flex-col gap-1">
        <Label variant="t1">Email Id</Label>
        <TextInput
          type="email"
          required
          defaultValue={email}
          noMargin
          placeholder="Enter email address"
          onChange={(e) => setFullNameEmail(e)}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label variant="t1">Full Name</Label>
        <TextInput
          type="text"
          noMargin
          required
          defaultValue={fullName}
          placeholder="Enter full name"
          onChange={(e) => setFullName(e)}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label variant="t1">Mobile no.</Label>
        <TextInput
          type="text"
          noMargin
          required
          defaultValue={mobile}
          placeholder="Enter mobile number"
          onChange={(e) => setMobile(e)}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label variant="t1">Country</Label>
        <DropDownSelector
          list={Countries}
          value={country}
          required
          placeholder="Select country"
          selectClassName="pl-0 pr-2"
          onStateChange={(e) => {
            setCountry(e);
            setState("");
          }}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label variant="t1">Address Line 1</Label>
        <TextInput
          type="text"
          noMargin
          required
          defaultValue={addressLine1}
          placeholder="Enter address line 1"
          onChange={(e) => setAddressLine1(e)}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label variant="t1">Address Line 2</Label>
        <TextInput
          type="text"
          noMargin
          defaultValue={addressLine2}
          placeholder="Enter address line 2"
          onChange={(e) => setAddressLine2(e)}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label variant="t1">State</Label>

        <Fold
          value={["India", "United States"].includes(country)}
          ifPresent={() => (
            <DropDownSelector
              list={country === "India" ? IndianStates : UsStates}
              value={state}
              required
              selectClassName="pl-0 pr-2"
              placeholder="Select state"
              onStateChange={(e) => {
                setState(e);
              }}
            />
          )}
          ifAbsent={() => (
            <TextInput
              type="text"
              noMargin
              defaultValue={state}
              required
              placeholder="Enter state"
              onChange={(e) => setState(e)}
            />
          )}
        />
      </div>
      <Button label="Proceed" onClick={() => {}} />
    </form>
  );
}

interface AddressCheckoutProps {
  plan: any;
  user: any;
  community: any;
  isVisible: boolean;
  existingBillingAddress?: BillingDetails;
  setIsVisible: (isVisible: boolean) => void;
  onProceed: (billingInfo: BillingDetails) => void;
}

/**
 * Display subscription details model
 * @param {any} plan - subscription plan
 * @param {any} user - user object
 * @param {any} community - community object
 * @param {Boolean} isVisible - is model visible
 * @param {Function} setIsVisible - set model visibility
 */
export function AddressCheckout({
  community,
  user,
  plan,
  isVisible,
  existingBillingAddress,
  setIsVisible,
  onProceed = (_) => {},
}: AddressCheckoutProps) {
  if (!community || !isVisible) {
    return null;
  }
  return (
    <ModalForm
      hideHeader
      visible={isVisible}
      setVisible={setIsVisible}
      childrenClassName="AddressCheckout p-0"
      className="m-4 max-h-[80vh] w-full max-w-xl overflow-y-auto bg-background md:max-w-6xl">
      <div className="flex min-w-full flex-col place-content-center items-center gap-2 rounded bg-background p-4 py-10 md:flex-row md:items-start">
        <section className="flex w-full flex-col gap-2 md:w-1/2">
          <div className="flex items-center gap-2">
            <Fold
              value={community.logo}
              ifPresent={() => (
                <img src={community.logo} alt="Logo" className="h-14" />
              )}
              ifAbsent={() => (
                <div className="flex place-content-center items-center rounded bg-primary p-2 text-sm text-primary-foreground">
                  {community.name
                    .split(" ")
                    .map((word: string) => word[0])
                    .join("")}
                </div>
              )}
            />
            <Label variant="t1">{community.name}</Label>
          </div>
          <div className="mt-10">
            <TextButton
              label="Go Back"
              className="flex items-center gap-2"
              prefix={<UIcon icon="arrow-left" />}
              onClick={() => {
                setIsVisible(false);
              }}
            />
          </div>
          <PlanBillingDetails plan={plan} selected={true} />
        </section>
        <section className="w-full max-w-md">
          <BillingAddress
            plan={plan}
            user={user}
            community={community}
            onProceed={onProceed}
            existingBillingAddress={existingBillingAddress}
          />
        </section>
      </div>
    </ModalForm>
  );
}

function PlanBillingDetails({
  plan,
  mySubscription,
  selected = false,
  onSelect,
}: {
  plan: SubscriptionPlan;
  selected?: boolean;
  mySubscription?: MySubscription;
  onSelect?: (value: SubscriptionPlan) => void;
}) {
  const baseAmount = SubscriptionUtils.convertAmount(plan.subscriptionAmount);
  const taxAmount = baseAmount * ((plan.taxPercentage ?? 0) / 100);
  const currency =
    CurrencySymbol[plan.currency as keyof typeof CurrencySymbol] ||
    plan.currency;

  const totalAmount = SubscriptionUtils.calculateSubscriptionTotal(plan);

  /* Base Amount = Total / (1 + Percent/100) */
  // const baseAmount = amount / (1 + (plan?.taxPercentage ?? 0) / 100) ?? 0;

  return (
    <div
      className={cx(
        "flex flex-col gap-4 rounded border border-border px-4 py-3 hover:bg-background",
        {
          "border-primary": selected,
          "cursor-pointer": Boolean(onSelect),
        }
      )}
      onClick={() => {
        if (onSelect) {
          onSelect(plan);
        }
      }}>
      <div className="flex">
        <Radio
          selected={selected}
          className={cx("", {
            hidden: !onSelect,
          })}
          children={undefined}
        />
        <div className="ml-2 flex-grow">
          <div className="flex place-content-between">
            <span className="flex items-center gap-2">
              <Label variant="t1">{plan.name}</Label>
              <Fold
                value={
                  mySubscription?.subscriptionPlan?._id === plan.id ?? false
                }
                ifPresent={() => {
                  return (
                    <span className="rounded bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                      <I18>Current Plan</I18>
                    </span>
                  );
                }}
              />
            </span>
            <span>
              {baseAmount ? (
                <span className="whitespace-nowrap">
                  <Label>
                    {currency}
                    {baseAmount}{" "}
                  </Label>
                  <Label variant="s1">
                    {(plan?.recurringInterval ?? 0) > 0 &&
                      `per ${plan?.recurringInterval} month`}
                  </Label>
                </span>
              ) : (
                <Label translate variant="t1">
                  Free
                </Label>
              )}
            </span>
          </div>
          <div className="flex flex-row justify-between">
            {plan.description && (
              <div
                dangerouslySetInnerHTML={{ __html: plan.description }}
                // className="text-secondary-foreground/70 text-sm mt-2"
                className="NL2BR headings mt-2 max-h-96 overflow-y-auto"></div>
            )}
          </div>
        </div>
      </div>
      {/* Amount breakdown */}
      {plan.taxPercentage && plan.taxPercentage > 0 ? (
        <div className="Amount theme-bg-disable flex flex-col gap-4 rounded p-4">
          <div className="flex justify-between">
            <Label>{plan.name}</Label>
            <Label>
              {currency}&nbsp;
              {baseAmount}
            </Label>
          </div>
          <div className="flex justify-between">
            <Label>Tax at {plan.taxPercentage}%</Label>
            <Label>
              {currency}&nbsp;
              {taxAmount}
            </Label>
          </div>
          <hr />
          <div className="flex justify-between">
            <Label translate>Total</Label>
            <Label>
              {currency}&nbsp;
              {totalAmount}
            </Label>
          </div>
        </div>
      ) : null}
    </div>
  );
}
